import { RefObject, useEffect, useRef } from "react";
import { isBlockedByChildPopup } from "~/sharedComponents/hooks/useBlockPointerEvents";
import { CloseCallback, CloseReason } from "~/sharedHOC/ClickOutside/ClickOutside";

type ClickOutsideProps = {
    containers: RefObject<HTMLElement>[];
    closeActionHandler: CloseCallback;
    disabled?: boolean;
    /**
     * If true, popup will automatically detect if any child popup is open and, if so, won't close on ESC or click outside.
     */
    disableClosingIfChildPopupOpened?: boolean;
    /**
     * If specified and returns true, the popup won't be closed on clicks outside.
     */
    disableClosing?: (e: React.SyntheticEvent | UIEvent) => boolean;
};

export const useClickOutside = (currentProps: ClickOutsideProps) => {
    const propsRef = useRef(currentProps);
    propsRef.current = currentProps;

    const isMouseDownOutside = useRef(false);

    useEffect(() => {
        if (currentProps.disabled) {
            return;
        }

        const mouseDownHandler = (e: MouseEvent) => {
            const isContentBlockedByChildPopup =
                propsRef.current.disableClosingIfChildPopupOpened &&
                propsRef.current.containers.some(container => isBlockedByChildPopup(container.current));

            if (isContentBlockedByChildPopup) {
                return;
            }

            if (propsRef.current.disableClosing?.(e)) {
                return;
            }

            isMouseDownOutside.current = propsRef.current.containers.every(container =>
                isOutside(container?.current, e.target as HTMLElement)
            );
        };

        const clickHandler = (e: MouseEvent) => {
            if (isMouseDownOutside.current) {
                propsRef.current.closeActionHandler(CloseReason.CLICK_OUTSIDE, e);
            }
        };

        // detect click outside on mousedown because sometimes we remove target on the
        // click event and in this case it's impossible to find out if it's click outside or not.
        document.addEventListener("mousedown", mouseDownHandler);
        // fire clickOutside on click (not mouseup) because click is not dispatched
        // when user scrolls the page by dragging scrollbar.
        document.addEventListener("click", clickHandler);

        return () => {
            document.removeEventListener("mousedown", mouseDownHandler);
            document.removeEventListener("click", clickHandler);
        };
    }, [currentProps.disabled]);
};

const isOutside = (container: HTMLElement, element: HTMLElement) => {
    const isElementInsideContainer =
        container?.contains(element) ||
        // don't close popup when user clicks into dp calendar.
        element.closest?.(".ui-datepicker") ||
        // or into tooltip
        element.closest?.(".qtip");

    return !isElementInsideContainer;
};
